/**
 * RichTextEditor Component
 *
 * A rich text editor with markdown toolbar, emoji picker, and @mentions support.
 * Built on top of MentionInput with additional formatting controls.
 */
import React, { useState, useRef, useCallback } from 'react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import MentionInput from './MentionInput';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onMentionsChange?: (mentions: { userIds: string[]; agentIds: string[]; channelIds: string[] }) => void;
  projectId: string;
  workspaceId: string;
  placeholder?: string;
  className?: string;
  minHeight?: number;
  maxHeight?: number;
  disabled?: boolean;
  onSubmit?: () => void;
  showToolbar?: boolean;
}

interface EmojiData {
  native: string;
  shortcodes?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onMentionsChange,
  projectId,
  workspaceId,
  placeholder,
  className = '',
  minHeight = 100,
  maxHeight = 400,
  disabled = false,
  onSubmit,
  showToolbar = true,
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  // Insert text at cursor or wrap selection
  const insertMarkdown = useCallback((prefix: string, suffix: string = '', placeholder: string = '') => {
    // For now, just append to the end since we're using a textarea
    // In a more advanced implementation, we'd track cursor position
    const newValue = value + prefix + placeholder + suffix;
    onChange(newValue);
  }, [value, onChange]);

  // Insert emoji
  const handleEmojiSelect = (emoji: EmojiData) => {
    onChange(value + emoji.native);
    setShowEmojiPicker(false);
  };

  // Toolbar button component
  const ToolbarButton: React.FC<{
    onClick: () => void;
    title: string;
    children: React.ReactNode;
    active?: boolean;
  }> = ({ onClick, title, children, active = false }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
        ${active ? 'bg-blue-500/15 dark:bg-blue-900/30 text-blue-600' : 'text-gray-600 dark:text-gray-400'}
        disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );

  return (
    <div ref={editorRef} className={`border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      {showToolbar && (
        <div className="flex items-center gap-1 px-2 py-1 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          {/* Bold */}
          <ToolbarButton onClick={() => insertMarkdown('**', '**', 'bold')} title="Bold (Ctrl+B)">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" />
            </svg>
          </ToolbarButton>

          {/* Italic */}
          <ToolbarButton onClick={() => insertMarkdown('*', '*', 'italic')} title="Italic (Ctrl+I)">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 4h4M14 20h-4m2-16l-2 16" />
            </svg>
          </ToolbarButton>

          {/* Strikethrough */}
          <ToolbarButton onClick={() => insertMarkdown('~~', '~~', 'strikethrough')} title="Strikethrough">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v8m0 4v4M4 12h16" />
            </svg>
          </ToolbarButton>

          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Inline Code */}
          <ToolbarButton onClick={() => insertMarkdown('`', '`', 'code')} title="Inline Code">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          </ToolbarButton>

          {/* Code Block */}
          <ToolbarButton onClick={() => insertMarkdown('\n```\n', '\n```\n', 'code block')} title="Code Block">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </ToolbarButton>

          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Bullet List */}
          <ToolbarButton onClick={() => insertMarkdown('\n- ', '', 'list item')} title="Bullet List">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </ToolbarButton>

          {/* Numbered List */}
          <ToolbarButton onClick={() => insertMarkdown('\n1. ', '', 'list item')} title="Numbered List">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
            </svg>
          </ToolbarButton>

          {/* Quote */}
          <ToolbarButton onClick={() => insertMarkdown('\n> ', '', 'quote')} title="Quote">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </ToolbarButton>

          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Link */}
          <ToolbarButton onClick={() => insertMarkdown('[', '](url)', 'link text')} title="Insert Link">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </ToolbarButton>

          <div className="flex-1" />

          {/* Emoji Picker */}
          <div className="relative">
            <ToolbarButton
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              title="Insert Emoji"
              active={showEmojiPicker}
            >
              <span className="text-lg">&#128512;</span>
            </ToolbarButton>

            {showEmojiPicker && (
              <div className="absolute right-0 top-full z-50 mt-1">
                <div
                  className="fixed inset-0"
                  onClick={() => setShowEmojiPicker(false)}
                />
                <div className="relative">
                  <Picker
                    data={data}
                    onEmojiSelect={handleEmojiSelect}
                    theme="auto"
                    previewPosition="none"
                    skinTonePosition="search"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Editor Area */}
      <MentionInput
        value={value}
        onChange={onChange}
        onMentionsChange={onMentionsChange}
        projectId={projectId}
        workspaceId={workspaceId}
        placeholder={placeholder}
        minHeight={minHeight}
        maxHeight={maxHeight}
        disabled={disabled}
        onSubmit={onSubmit}
        className="border-0 rounded-none focus:ring-0"
      />

      {/* Footer with tips */}
      <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">@</kbd> mention
            {' '}
            <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">#</kbd> channel
            {' '}
            <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">```</kbd> code
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">Ctrl</kbd>
            +
            <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">Enter</kbd>
            {' '}to send
          </span>
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;
